import React, { useEffect } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useSelector, useDispatch } from 'react-redux';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import Tooltip from 'components/Tooltip';
import { KeyboardKey } from 'const';
import { isDraggingAnchor as isDraggingAnchorSelector } from 'containers/App/selectors';
import { removeAnchor } from 'containers/Documents/actions';
import { intlGet } from 'utils/intlGet';
import { isMac } from 'utils/platform';
import styles from './styles.module.scss';
import { useAnchorDragSource } from './useAnchorDragSource';

type AnchorProps = {
  anchorBlockKey: string | undefined;
  documentId: string;
  relationId: string;
  offsetTop: number;
};

const Anchor: React.FunctionComponent<AnchorProps> = (props) => {
  const {
    anchorBlockKey,
    documentId,
    offsetTop,
    relationId,
  } = props;
  const dispatch = useDispatch();
  const isDraggingAnchor = useSelector(isDraggingAnchorSelector);
  const isAnchorApplied = !!anchorBlockKey;
  const isDisplayed = isAnchorApplied || isDraggingAnchor;
  const { isDragging, dragRef, preview } = useAnchorDragSource({
    anchorBlockKey,
    documentId,
    relationId,
  });
  const [isTooltipShown, setIsTooltipShown] = React.useState(false);
  const anchorIconRef = React.createRef<HTMLDivElement>();

  const [isActive, setIsActive] = React.useState(false);

  const button = React.useRef<HTMLButtonElement>(null);

  const handleClick = React.useCallback(
    (event: MouseEvent) => {
      const { current: buttonElement } = button;
      if (!buttonElement) {
        return;
      }

      const clickOutside = !buttonElement.contains(event.target as Node);
      if (clickOutside) {
        setIsActive(false);
      } else if (button.current) {
        // HACK: need to focus, because keyboard events are not handled in Safari
        button.current.focus();
      }
    },
    [button],
  );

  useEffect(() => {
    if (isActive && !isDisplayed) {
      setIsActive(false);
    }
    if (!isActive) {
      document.removeEventListener('click', handleClick);
    }
  }, [handleClick, isActive, isDisplayed]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  if (!isDisplayed) {
    return null;
  }

  if (isDisplayed && !isAnchorApplied) {
    return <div className={styles.placeholder} style={{ top: offsetTop }} />;
  }

  const handleDeletePress: React.KeyboardEventHandler<HTMLButtonElement> = ({ key }) => {
    const { platform } = window.navigator;

    if (isMac(platform) ? key === KeyboardKey.BACKSPACE : key === KeyboardKey.DELETE) {
      if (!anchorBlockKey) {
        return;
      }
      dispatch(removeAnchor(documentId, anchorBlockKey, relationId));
    }
  };

  const enableEditing = (): void => {
    setIsActive(true);
    // HACK: need to handle clicks and set focus, because onBlur event in Safari doesn't work or works unpredictable
    document.addEventListener('click', handleClick);
    // HACK: need to focus, because keyboard events are not handled in Safari
    if (button.current) {
      button.current.focus();
    }
  };

  const showTooltip = (): void => setIsTooltipShown(true);
  const hideTooltip = (): void => setIsTooltipShown(false);

  const handleDragStart = (event: React.DragEvent): void => {
    const obj = JSON.stringify({ anchorBlockKey, relationId, documentId });
    event.dataTransfer.setData('text/plain', obj);
  };

  return (
    <button
      ref={button}
      onKeyUp={handleDeletePress}
      onClick={enableEditing}
      className={styles.Anchor}
      style={{ top: offsetTop }}
    >
      <div className={styles.triangle} />
      <hr className={styles.line} />
      {isActive && dragRef(
        <div className={styles.icon} ref={anchorIconRef} onMouseEnter={showTooltip} onMouseLeave={hideTooltip} onDragStart={handleDragStart}>
          <Icon type={IconType.ANCHOR} size="sm" color="secondary" />
          <Tooltip
            className={styles.tooltip}
            parent={anchorIconRef}
            show={!isDragging && isTooltipShown}
            text={intlGet('Anchor.Tooltip', 'MoveAnchor')}
          />
        </div>,
      )}
    </button>
  );
};

export default Anchor;
